import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/shared/http';
import { action, type ScreenAction } from '@/shared/screen';

const ORDER_BY = /^[A-Za-z][\w.]*_(ASC|DESC)$/;

export interface ListSearchOptions {
  /** Tamanhos aceitos na URL (padrão 5/10/15/20, do legado). */
  readonly sizes?: readonly number[];
}

/**
 * Schema do `validateSearch` de uma lista (U07, U39 §2): `page` (a partir de 1, como o operador lê),
 * `size`, `orderBy` (`campo_ASC|DESC`) e os filtros da tela. Tudo opcional: valor inválido cai fora
 * da URL em vez de quebrar a tela, e o padrão não aparece no link.
 */
export function listSearchSchema<F extends z.ZodRawShape>(
  filters: F,
  options: ListSearchOptions = {},
) {
  const sizes = options.sizes ?? PAGE_SIZE_OPTIONS;
  const schema = z.object({
    page: z.coerce.number().int().min(1).optional().catch(undefined),
    size: z.coerce
      .number()
      .int()
      .refine((value) => sizes.includes(value))
      .optional()
      .catch(undefined),
    orderBy: z.string().regex(ORDER_BY).optional().catch(undefined),
    ...filters,
  });
  return z.unknown().transform((raw) => {
    const parsed = schema.parse(raw);
    const keys = raw !== null && typeof raw === 'object' ? Object.keys(raw) : [];
    return Object.fromEntries(
      [...keys, ...Object.keys(parsed).filter((key) => !keys.includes(key))]
        .filter((key) => key in parsed)
        .map((key) => [key, (parsed as Record<string, unknown>)[key]]),
    ) as typeof parsed;
  });
}

/** O que a URL da lista carrega, fora os filtros. */
export interface ListSearch {
  readonly page?: number | undefined;
  readonly size?: number | undefined;
  readonly orderBy?: string | undefined;
}

/** Params do bloco da lista a partir da URL: página a partir de 0 (o fio do legado) e tamanho padrão 5. */
export function toListParams<S extends ListSearch>(
  search: S,
  options: { readonly defaultSize?: number } = {},
) {
  const { page, size, orderBy, ...filters } = search;
  return {
    ...filters,
    page: Math.max(0, (page ?? 1) - 1),
    size: size ?? options.defaultSize ?? DEFAULT_PAGE_SIZE,
    ...(orderBy ? { orderBy } : {}),
  };
}

/** Patch da URL para uma página nova (0-based → 1-based), sem o padrão no link. */
export function pageSearchPatch(
  next: { readonly page: number; readonly size: number; readonly orderBy?: string | undefined },
  options: { readonly defaultSize?: number } = {},
) {
  const defaultSize = options.defaultSize ?? DEFAULT_PAGE_SIZE;
  return {
    page: next.page > 0 ? next.page + 1 : undefined,
    size: next.size === defaultSize ? undefined : next.size,
    orderBy: next.orderBy,
  };
}

/** Entrada da ação de página: estrita, como toda ferramenta do agente (sem alvo nem filtro a mais). */
export const pageActionInput = z.strictObject({
  page: z.number().int().min(0),
  size: z.number().int().min(1).max(100),
  orderBy: z.string().regex(ORDER_BY).optional(),
});

/**
 * A ação de navegação que o `DataView` pede para mudar de página, tamanho ou ordem. A página vem a
 * partir de 0 (o `params` do bloco) e vira a URL da lista. Serve ao agente como "filtrar a lista".
 */
export function listPageAction(
  id: string,
  options: { readonly defaultSize?: number } = {},
): ScreenAction<z.infer<typeof pageActionInput>> {
  return action({
    id,
    kind: 'navigate',
    page: true,
    label: msg`Mudar de página`,
    description: msg`Muda a página (começa em 0), o tamanho da página ou a ordem (campo_ASC ou campo_DESC) da lista da tela.`,
    input: pageActionInput,
    run: ({ nav, input }) => nav.search(pageSearchPatch(input, options)),
  });
}
